/**
 * RAG（检索增强生成）相关API服务
 */
import api from './api';

export interface RAGGenerationOptions {
  item_id: string;
  model_config_id: number;
  custom_prompt?: string;
  use_web_search?: boolean;
  search_depth?: 'quick' | 'normal' | 'deep';
  max_search_results?: number;
}

export interface Reference {
  id: number;
  chapter_id: string;
  title: string;
  url: string;
  content?: string;
  relevance_score?: number;
  used_in_generation: boolean;
  user_marked: boolean;
  created_at: string;
}

export interface SearchHistory {
  id: number;
  chapter_id: string;
  keywords: string[];
  search_results: Array<{
    title: string;
    url: string;
    snippet: string;
  }>;
  created_at: string;
}

export interface SearchConfig {
  bing_api_key_configured: boolean;
  preferred_engine: 'bing' | 'duckduckgo' | 'both';
  default_search_depth: 'quick' | 'normal' | 'deep';
  default_max_results: number;
}

/**
 * 获取项目的参考资料
 */
export const getProjectReferences = async (
  projectId: number,
  chapterId?: string
): Promise<Reference[]> => {
  const params = chapterId ? { chapter_id: chapterId } : {};
  const response = await api.get(`/api/projects/${projectId}/references`, { params });
  return response.data;
};

/**
 * 标记参考资料
 */
export const markReference = async (
  projectId: number,
  referenceId: number,
  marked: boolean
): Promise<void> => {
  await api.post(`/api/projects/${projectId}/references/${referenceId}/mark`, {
    marked
  });
};

/**
 * 删除参考资料
 */
export const deleteReference = async (projectId: number, referenceId: number) => {
  await api.delete(`/api/projects/${projectId}/references/${referenceId}`);
};

/**
 * 获取搜索历史
 */
export const getSearchHistory = async (
  projectId: number,
  chapterId?: string
): Promise<SearchHistory[]> => {
  const params = chapterId ? { chapter_id: chapterId } : {};
  const response = await api.get(`/api/projects/${projectId}/search-history`, { params });
  return response.data;
};

/**
 * 获取搜索统计
 */


/**
 * 获取搜索配置
 */
export const getSearchConfig = async (): Promise<SearchConfig> => {
  const response = await api.get('/api/projects/search/config');
  return response.data;
};

/**
 * 更新搜索配置
 */
export const updateSearchConfig = async (config: Partial<SearchConfig>): Promise<void> => {
  await api.put('/api/projects/search/config', config);
};

/**
 * 删除搜索配置（还原为系统默认值）
 */
export const deleteSearchConfig = async (): Promise<void> => {
  await api.delete('/api/projects/search/config');
};

/**
 * RAG生成内容（流式）
 * 返回一个可读流，需要手动处理SSE
 */
export const generateContentWithRAG = async (
  projectId: number,
  options: RAGGenerationOptions
): Promise<Response> => {
  const token = localStorage.getItem('token');
  
  const response = await fetch(
    `${api.defaults.baseURL}/api/projects/${projectId}/generate/content-rag`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(options),
    }
  );

  if (!response.ok) {
    throw new Error('RAG生成失败');
  }

  return response;
};

export interface HistoricalCandidate {
  id: number;
  project_name: string;
  completed_at: string;
  indexed_chapters: number;
  has_content: boolean;
}

export const getHistoricalCandidates = async (excludeProjectId?: number): Promise<HistoricalCandidate[]> => {
  const params = excludeProjectId ? { exclude_project_id: excludeProjectId } : {};
  const response = await api.get('/api/projects/search/historical-candidates', { params });
  return response.data;
};

/**
 * 重新索引项目（用于历史方案检索）
 */
export const reindexProject = async (projectId: number): Promise<{ indexed_chapters: number }> => {
  const response = await api.post(`/api/projects/${projectId}/reindex`);
  return response.data;
};

/**
 * 获取历史项目的章节树（含全文内容）
 */
export const getHistoricalChapters = async (
  projectIds: number[]
): Promise<import('../types').HistoricalChaptersResponse> => {
  const response = await api.get('/api/projects/search/historical-chapters', {
    params: { project_ids: projectIds.join(',') }
  });
  return response.data;
};

/**
 * 按需获取历史项目单个章节的正文内容
 */
export const getHistoricalChapterContent = async (
  projectId: number,
  chapterPath: string,
): Promise<string> => {
  const response = await api.get('/api/projects/search/historical-chapter-content', {
    params: { project_id: projectId, chapter_path: chapterPath }
  });
  return response.data.content || '';
};

// ── 章节推荐 ──

export interface ChapterRefSummary {
  project_id: number;
  project_name: string;
  chapter_path: string;
  chapter_title: string;
  description: string;
}

export interface RecommendResult {
  project_id: number;
  chapter_path: string;
  reason: string;
  project_name?: string;
}

export interface RecommendHistoryItem {
  id: number;
  query: string;
  result: RecommendResult[];
  created_at: string;
}

export interface RecommendResponse {
  recommendations: RecommendResult[];
  total_candidates: number;
  project_count: number;
}

/**
 * LLM 智能推荐历史章节
 * availableChapterRefs 为空时自动触发全库搜索
 */
export const recommendChapters = async (
  projectId: number,
  query: string,
  projectName: string,
  projectOverview: string,
  availableChapterRefs: ChapterRefSummary[],
  excludeProjectId?: number,
): Promise<RecommendResponse> => {
  const response = await api.post('/api/projects/search/recommend-chapters', {
    project_id: projectId,
    query,
    project_name: projectName,
    project_overview: projectOverview,
    available_chapter_refs: availableChapterRefs,
    exclude_project_id: excludeProjectId,
  });
  return response.data;
};

/**
 * 获取推荐历史
 */
export const getChapterRecommendations = async (
  projectId: number,
): Promise<RecommendHistoryItem[]> => {
  const response = await api.get(`/api/projects/${projectId}/chapter-recommendations`);
  return response.data.history;
};

/**
 * 删除推荐历史记录
 */
export const deleteChapterRecommendation = async (recId: number): Promise<void> => {
  await api.delete(`/api/projects/chapter-recommendations/${recId}`);
};

/**
 * 重新执行推荐
 */
export const rerunChapterRecommendation = async (
  recId: number,
  projectId: number,
  projectName: string,
  projectOverview: string,
): Promise<RecommendHistoryItem> => {
  const response = await api.post(`/api/projects/chapter-recommendations/${recId}/rerun`, {
    project_id: projectId,
    project_name: projectName,
    project_overview: projectOverview,
  });
  return response.data;
};

// ── 参考资料上传 + 智能建议 ──

/**
 * 上传参考文件（multipart/form-data）
 */
export const uploadReferenceFile = async (
  projectId: number,
  formData: FormData
) => {
  const response = await api.post(
    `/api/projects/${projectId}/references/upload-file`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

/**
 * 根据当前章节内容智能建议参考资料
 */
export const suggestReferences = async (
  projectId: number,
  chapterId: string,
  body: {
    current_content: string;
    chapter_title: string;
    chapter_description?: string;
    requirements?: string;
  }
) => {
  const response = await api.post(
    `/api/projects/${projectId}/chapters/${chapterId}/suggest-references`,
    body,
    { timeout: 60000 }
  );
  return response.data;
};
